import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Home,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Building,
  Search,
  Filter,
  IndianRupee,
  Ruler,
  BedDouble,
  LogOut
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const PropertySubmissions = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/property-submissions`);
      setSubmissions(res.data.items || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load property submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    setActionLoading(true);
    try {
      await api.patch(`/property-submissions/${submissionId}/approve`);
      toast.success('Property approved and listed successfully!');
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error('Failed to approve property');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (submissionId) => {
    setActionLoading(true);
    try {
      await api.patch(`/property-submissions/${submissionId}/reject`, { reason: rejectReason });
      toast.success('Property submission rejected');
      setSelectedSubmission(null);
      setRejectReason('');
      fetchSubmissions();
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error('Failed to reject property');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} flex items-center gap-1`} data-testid={`status-badge-${status}`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredSubmissions = submissions.filter(s => {
    const matchesSearch =
      s.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.propertyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.ownerPhone.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === 'pending').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    rejected: submissions.filter(s => s.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="property-submissions-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Property Submissions</h1>
                <p className="text-sm text-gray-600">Review and approve property listings</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/admin/dashboard')} variant="outline" data-testid="dashboard-link-btn">
                <Home className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button onClick={handleLogout} variant="outline" className="text-red-600 hover:bg-red-50" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-total">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-600">Total Submissions</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-600">Pending Review</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-approved">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
              <p className="text-sm text-gray-600">Approved</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-rejected">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by owner name, property or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        <Card>
          <CardHeader>
            <CardTitle>Submissions ({filteredSubmissions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredSubmissions.length === 0 ? (
              <div className="text-center py-12" data-testid="empty-state">
                <Building className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No property submissions found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSubmissions.map((submission) => (
                  <Card key={submission.id} className="hover:shadow-md transition-shadow" data-testid={`submission-card-${submission.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">{submission.propertyName}</h3>
                            {getStatusBadge(submission.status)}
                            <Badge variant="outline">{submission.propertyType}</Badge>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600 mb-3">
                            <div className="flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                              <span className="truncate">{submission.ownerName} - {submission.ownerPhone}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                              <span className="truncate">{submission.location}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <IndianRupee className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                              <span>
                                {submission.price}
                                {submission.listingType === 'rent' && ' / month'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                              <span>{formatDate(submission.createdAt)}</span>
                            </div>
                          </div>

                          {submission.bhk && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                              <BedDouble className="h-3.5 w-3.5 text-blue-600" />
                              <span>{submission.bhk}</span>
                              <span className="mx-1">|</span>
                              <Ruler className="h-3.5 w-3.5 text-blue-600" />
                              <span>{submission.area}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedSubmission(submission)}
                            data-testid={`view-btn-${submission.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {submission.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApprove(submission.id)}
                                disabled={actionLoading}
                                data-testid={`approve-btn-${submission.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSubmission(null)} data-testid="submission-detail-modal">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  Submission Details
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setSelectedSubmission(null)} data-testid="close-modal-btn">
                  <XCircle className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600">Status:</span>
                {getStatusBadge(selectedSubmission.status)}
                {selectedSubmission.status === 'rejected' && selectedSubmission.rejectionReason && (
                  <span className="text-sm text-red-600">- {selectedSubmission.rejectionReason}</span>
                )}
              </div>

              {/* Owner Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Owner Information</h4>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">{selectedSubmission.ownerName}</span> - 
                    <a href={`tel:${selectedSubmission.ownerPhone}`} className="text-blue-600 hover:underline">{selectedSubmission.ownerPhone}</a>
                  </div>
                  {selectedSubmission.ownerEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      <a href={`mailto:${selectedSubmission.ownerEmail}`} className="text-blue-600 hover:underline">{selectedSubmission.ownerEmail}</a>
                    </div>
                  )}
                </div>
              </div>

              {/* Property Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Property Details</h4>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-gray-500">Property Name</label>
                    <p className="font-medium text-gray-900">{selectedSubmission.propertyName}</p>
                  </div>
                  <div>
                    <label className="text-gray-500">Property Type</label>
                    <p className="font-medium text-gray-900">{selectedSubmission.propertyType}</p>
                  </div>
                  <div>
                    <label className="text-gray-500">Location</label>
                    <p className="font-medium text-gray-900">{selectedSubmission.location}</p>
                  </div>
                  <div>
                    <label className="text-gray-500">
                      {selectedSubmission.listingType === 'rent' ? 'Expected Rent' : 'Expected Price'}
                    </label>
                    <p className="font-medium text-gray-900">
                      {selectedSubmission.price}
                      {selectedSubmission.listingType === 'rent' && ' / month'}
                    </p>
                  </div>
                  {selectedSubmission.bhk && (
                    <div>
                      <label className="text-gray-500">BHK</label>
                      <p className="font-medium text-gray-900">{selectedSubmission.bhk}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-gray-500">Area</label>
                    <p className="font-medium text-gray-900">{selectedSubmission.area}</p>
                  </div>
                  <div>
                    <label className="text-gray-500">Submitted On</label>
                    <p className="font-medium text-gray-900">{formatDate(selectedSubmission.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedSubmission.description && (
                <div>
                  <label className="text-sm text-gray-500">Description</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{selectedSubmission.description}</p>
                </div>
              )}

              {/* Images */}
              {selectedSubmission.images && selectedSubmission.images.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Property Images</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                    {selectedSubmission.images.map((img, i) => (
                      <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                        <img src={img} alt={`Property ${i + 1}`} className="w-full h-32 object-cover rounded-lg border hover:opacity-80 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Amenities & Highlights */}
              {selectedSubmission.amenities && selectedSubmission.amenities.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Amenities</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedSubmission.amenities.map((a, i) => (
                      <Badge key={i} variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">{a}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedSubmission.highlights && selectedSubmission.highlights.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Highlights</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedSubmission.highlights.map((h, i) => (
                      <Badge key={i} variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">{h}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedSubmission.status === 'pending' && (
                <div className="border-t pt-6 space-y-4">
                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedSubmission.id)}
                      disabled={actionLoading}
                      data-testid="modal-approve-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {actionLoading ? 'Processing...' : 'Approve & List Property'}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Reason for rejection (optional)..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      data-testid="reject-reason-input"
                    />
                    <Button
                      variant="outline"
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => handleReject(selectedSubmission.id)}
                      disabled={actionLoading}
                      data-testid="modal-reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {actionLoading ? 'Processing...' : 'Reject Submission'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PropertySubmissions;
